import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, KeyRound, LogIn, MoreHorizontal } from "lucide-react";
import { useClerk } from "@clerk/react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const fmtTs = (v) => (v ? fmtDate(typeof v === "number" ? new Date(v).toISOString() : v, true) : "—");
const STATUS = { active: "Active", revoked: "Révoquée", ended: "Terminée", expired: "Expirée", removed: "Supprimée", abandoned: "Abandonnée", pending: "En attente" };

function SessionsDialog({ user, open, onOpenChange }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) { setData(null); return; }
    api.get(`/admin/users/${user.user_id}/sessions`).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
  }, [open, user.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="user-sessions-dialog">
        <DialogHeader><DialogTitle>Connexions de {user.name || user.email}</DialogTitle></DialogHeader>
        {!data ? <p className="text-sm text-slate-500">Chargement…</p> : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Sessions</div>
              {data.sessions.length === 0 && <p className="text-sm text-slate-500">Aucune session.</p>}
              {data.sessions.map((s) => (
                <div key={s.id} className="text-sm border-b py-1.5 flex flex-wrap gap-x-3" data-testid={`user-session-${s.id}`}>
                  <span className={s.status === "active" ? "text-emerald-700 font-medium" : "text-slate-500"}>{s.status === "active" ? "Active" : STATUS[s.status] || s.status}</span>
                  <span>Ouverte {fmtTs(s.created_at)}</span><span>Dernière activité {fmtTs(s.last_active_at)}</span>
                  {(s.browser || s.device) && <span>{[s.browser, s.device].filter(Boolean).join(" · ")}</span>}
                  {(s.city || s.ip) && <span className="text-slate-500">{[s.city, s.country, s.ip].filter(Boolean).join(" · ")}</span>}
                  {s.impersonated && <span className="text-amber-700">ouverte par un administrateur</span>}
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Historique des connexions</div>
              {data.logins.map((l, i) => (
                <div key={i} className="text-sm border-b py-1.5 flex gap-3" data-testid="user-login-row">
                  <span className="w-36 shrink-0">{fmtDate(l.at, true)}</span>
                  <span className="text-slate-500 truncate">{l.action === "connexion_remplacement" ? "Par un administrateur · " : ""}{l.ip} · {l.details}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function UserAccountActions({ user, isSelf }) {
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const isAdmin = user.role === "admin";
  const reset = async () => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${user.email} ? Il sera déconnecté partout et devra choisir un nouveau mot de passe via « Mot de passe oublié » à sa prochaine connexion.`)) return;
    try { await api.post(`/admin/users/${user.user_id}/password-reset`); toast.success("Mot de passe réinitialisé : la personne choisira elle-même le nouveau"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const impersonate = async () => {
    if (!window.confirm(`Vous connecter à la place de ${user.email} ? Votre session administrateur sera fermée ; vous verrez exactement ce que voit cette personne pendant 30 minutes maximum. L'action est tracée.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${user.user_id}/impersonate`);
      const ticket = data.token || new URL(data.url).searchParams.get("ticket");
      // Full page load: Clerk only consumes __clerk_ticket on initial load (client-side redirect drops it)
      await signOut();
      window.location.assign(`/sign-in?__clerk_ticket=${encodeURIComponent(ticket)}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`user-account-menu-${user.user_id}`}><MoreHorizontal className="h-4 w-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setOpen(true)} data-testid={`user-sessions-${user.user_id}`}><History className="h-4 w-4 mr-2" />Historique des connexions</DropdownMenuItem>
          {!isSelf && <DropdownMenuItem onClick={reset} data-testid={`user-reset-${user.user_id}`}><KeyRound className="h-4 w-4 mr-2" />Réinitialiser le mot de passe</DropdownMenuItem>}
          {!isSelf && !isAdmin && user.active !== false && <DropdownMenuItem onClick={impersonate} data-testid={`user-impersonate-${user.user_id}`}><LogIn className="h-4 w-4 mr-2" />Se connecter à sa place</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
      <SessionsDialog user={user} open={open} onOpenChange={setOpen} />
    </>
  );
}
